import React, { useRef, useState } from 'react';
import { withStyles } from 'tss-react/mui';
import { Grid, Button, Box } from '@mui/material';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';

const styles = {
  container: {
    border: '1px solid rgba(0, 0, 0, 0.23)',
    width: '100%',
    padding: '20px',
    margin: '8px',
    borderRadius: '4px',
    position: 'relative'
  },
  label: {
    marginLeft: '10px',
    padding: '4px',
    fontSize: '1rem',
    color: 'rgba(0, 0, 0, 0.54)',
    backgroundColor: 'white',
    position: 'absolute',
    top: '-8px',
    left: '-10px',
    transform: 'translate(14px, -6px) scale(0.75)'
  },
  error: {
    color: 'red',
    fontSize: '0.875rem',
    marginTop: '8px'
  },
  fileInput: {
    display: 'block',
    marginBottom: '10px'
  },
  cropperContainer: {
    width: '100%',
    height: '300px'
  },
  previewImage: {
    maxWidth: '200px',
    maxHeight: '200px'
  }
};

const ImageUploadWithCrop = ({ label, name, value, onChange, classes }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImage, setCroppedImage] = useState(value || null);
  const [error, setError] = useState('');
  const cropperRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File size exceeds 5MB limit. Please upload a smaller image.');
        setImageSrc(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const { width, height } = img;
        if (width < 622 || height < 350) {
          setError(
            'Image dimensions must be at least 622x350 pixels to support a 16:9 aspect ratio.'
          );
          setImageSrc(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          URL.revokeObjectURL(img.src);
          return;
        }

        setError('');
        setImageSrc(URL.createObjectURL(file));
        setCroppedImage(null);
      };
      img.onerror = () => {
        setError('Failed to load the image. Please try a different file.');
        setImageSrc(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        URL.revokeObjectURL(img.src);
      };
    }
  };

  const handleCropSave = () => {
    if (cropperRef.current && cropperRef.current.cropper) {
      const croppedCanvas = cropperRef.current.cropper.getCroppedCanvas();
      if (croppedCanvas.width < 622 || croppedCanvas.height < 350) {
        setError(
          'Cropped image must be at least 400x350 pixels (minimum 622x350 pixels to maintain a 16:9 aspect ratio).'
        );
        setImageSrc(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      croppedCanvas.toBlob((blob) => {
        const file = new File([blob], 'cropped-image.jpg', {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
        setCroppedImage(URL.createObjectURL(file));
        setImageSrc(null);
        setError('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        onChange(name, file);
      }, 'image/jpeg');
    }
  };

  const handleRemove = () => {
    setImageSrc(null);
    setCroppedImage(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onChange(name, null);
  };

  return (
    <Grid container alignItems="center" justifyContent="left">
      <div className={classes.container}>
        <div className={classes.label}>{label}</div>
        {!imageSrc && !croppedImage && (
          <Box>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              ref={fileInputRef}
              className={classes.fileInput}
            />
          </Box>
        )}

        {imageSrc && (
          <Box>
            <div className={classes.cropperContainer}>
              <Cropper
                ref={cropperRef}
                src={imageSrc}
                style={{ height: '100%', width: '100%' }}
                guides
                viewMode={1}
                minCropBoxWidth={622}
                minCropBoxHeight={350}
                aspectRatio={16 / 9}
                background={false}
                responsive
                autoCropArea={1}
                checkOrientation={false}
              />
            </div>
            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setImageSrc(null);
                  setError('');
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCropSave}
              >
                Save Crop
              </Button>
            </Box>
          </Box>
        )}

        {croppedImage && (
          <Box mt={2}>
            <img
              src={croppedImage}
              alt="Cropped Preview"
              className={classes.previewImage}
            />
            <Box mt={1}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleRemove}
              >
                Remove Image
              </Button>
            </Box>
          </Box>
        )}

        {error && <div className={classes.error}>{error}</div>}
      </div>
    </Grid>
  );
};

export default withStyles(ImageUploadWithCrop, styles);
